import { useMemo } from 'react';
import type { Column, Rec } from './api';
import type { Computed } from './formula/engine';

export type Sort = { colId: string; dir: 'asc' | 'desc' } | null;

const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(String(v).replace(',', '.').replace(/\s/g, ''));
  return Number.isFinite(n) ? n : null;
};

/**
 * Оптимизированный хук для фильтрации, поиска и сортировки строк:
 * - O(N) Schwartzian Transform для сверхбыстрой сортировки без повторных вычислений
 * - Предварительное разрешение колонок фильтрации перед циклом
 * - Быстрый поиск совпадений без аллокаций
 */
export function useView(
  columns: Column[],
  records: Rec[],
  query: string,
  sort: Sort,
  computed?: Map<string, Computed>,
  /** null — искать по всем колонкам. */
  searchColId: string | null = null,
  /** По колонке: набор разрешённых значений. Пустая строка — пустые ячейки. */
  filters?: Map<string, Set<string>>,
): Rec[] {
  return useMemo(() => {
    if (!records.length) return records;

    const term = query.trim().toLowerCase();

    /** Значение для поиска и сортировки: у формульных колонок — вычисленное. */
    const valueOf = (rec: Rec, col: Column): unknown => {
      const calc = computed?.get(`${rec.id}:${col.fields.colId}`);
      if (calc) return calc.isError ? calc.formatted : calc.value;
      return rec.fields[col.fields.colId];
    };

    let rows = records;

    // 1. Быстрый поиск по строкам
    if (term) {
      const scope = searchColId
        ? columns.filter((c) => c.fields.colId === searchColId)
        : columns;

      rows = rows.filter((rec) => {
        for (let i = 0; i < scope.length; i++) {
          const v = valueOf(rec, scope[i]);
          if (v !== null && v !== undefined && String(v).toLowerCase().includes(term)) {
            return true;
          }
        }
        return false;
      });
    }

    // 2. Предварительно разрешённые фильтры колонок (без повторного find на каждой строке)
    if (filters && filters.size > 0) {
      const activeFilters: { col: Column; allowed: Set<string> }[] = [];
      for (const [colId, allowed] of filters) {
        const col = columns.find((c) => c.fields.colId === colId);
        if (col) activeFilters.push({ col, allowed });
      }

      if (activeFilters.length > 0) {
        rows = rows.filter((rec) => {
          for (let i = 0; i < activeFilters.length; i++) {
            const { col, allowed } = activeFilters[i];
            const v = valueOf(rec, col);
            const strVal = v === null || v === undefined ? '' : String(v);
            if (!allowed.has(strVal)) return false;
          }
          return true;
        });
      }
    }

    // 3. Сверхбыстрая сортировка через Schwartzian transform
    if (sort) {
      const col = columns.find((c) => c.fields.colId === sort.colId);
      if (col && rows.length > 1) {
        const factor = sort.dir === 'asc' ? 1 : -1;

        // Предварительно вычисляем ключи сортировки за 1 проход O(N)
        const mapped = new Array(rows.length);
        for (let i = 0; i < rows.length; i++) {
          const rec = rows[i];
          const v = valueOf(rec, col);
          const empty = v === null || v === undefined || v === '';
          let n: number | null = null;
          let s = '';

          if (!empty) {
            n = num(v);
            if (n === null) s = String(v);
          }

          mapped[i] = { rec, empty, n, s };
        }

        mapped.sort((a, b) => {
          // Пустые значения всегда внизу
          if (a.empty || b.empty) return a.empty === b.empty ? 0 : a.empty ? 1 : -1;
          if (a.n !== null && b.n !== null) return (a.n - b.n) * factor;
          return collator.compare(a.s, b.s) * factor;
        });

        rows = mapped.map((m) => m.rec);
      }
    }

    return rows;
  }, [columns, records, query, sort, computed, searchColId, filters]);
}

/** Уникальные значения колонки для списка в фильтре (с кэшированием Set). */
export function uniqueValues(
  column: Column | undefined,
  records: Rec[],
  computed?: Map<string, Computed>,
): string[] {
  if (!column || !records.length) return [];

  const set = new Set<string>();
  const colId = column.fields.colId;

  for (let i = 0; i < records.length; i++) {
    const rec = records[i];
    const calc = computed?.get(`${rec.id}:${colId}`);
    const v = calc ? calc.value : rec.fields[colId];
    set.add(v === null || v === undefined ? '' : String(v));
  }

  return Array.from(set).sort(collator.compare);
}
